import { createFileRoute, Link } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

export const Route = createFileRoute("/refunds")({
  component: RefundsPage,
  head: () => ({
    meta: [
      { title: "Refund Policy — JobYoda" },
      { name: "description", content: "JobYoda 30-day money-back guarantee and refund process." },
      { property: "og:title", content: "Refund Policy — JobYoda" },
      { property: "og:description", content: "JobYoda 30-day money-back guarantee and refund process." },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function RefundsPage() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Button component={Link} to="/" size="small" color="inherit" sx={{ mb: 2 }}>
          ← Back to home
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Refund Policy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" color="text.secondary">
          JobYoda is operated by Hypertime LLC. We want you to be happy with your
          subscription. If JobYoda isn't the right fit, you can request a full refund
          under the terms below.
        </Typography>

        <Section title="30-day money-back guarantee">
          We offer a 30-day money-back guarantee on all paid plans. If you are not
          satisfied with your purchase, you can request a full refund within 30 days of
          your order date — no questions asked.
        </Section>

        <Section title="How to request a refund">
          To request a refund, contact us via our{" "}
          <Link to="/support" style={{ color: "inherit", fontWeight: 600 }}>support page</Link>{" "}
          with the email address you used at checkout. We'll process the refund
          through Stripe within 2 business days. Refunds are returned to the
          original payment method, typically within 5–10 business days depending
          on your bank.
        </Section>

        <Section title="Subscription renewals">
          Paid plans renew automatically. You can cancel anytime from the
          billing page in your account; access continues until the end of the
          current billing period. For renewal charges, please contact support
          promptly if you'd like a refund — we'll review these requests on a
          fair, good-faith basis.
        </Section>

        <Section title="Payment processing">
          Payments are processed by <strong>Stripe</strong>. Stripe handles card
          authorization and settlement. Refund and dispute mechanics for card
          payments follow Stripe's standard processing rules.
        </Section>


        <Section title="Contact">
          Questions? Reach us via our{" "}
          <Link to="/support" style={{ color: "inherit", fontWeight: 600 }}>support page</Link>.
        </Section>

        <Divider sx={{ my: 5 }} />
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={Link} to="/terms" variant="outlined">Terms</Button>
          <Button component={Link} to="/privacy" variant="outlined">Privacy</Button>
          <Button component={Link} to="/support" variant="outlined">Support</Button>
          <Button component={Link} to="/signup" variant="contained">Get started</Button>
        </Stack>
      </Container>
    </Box>
  );
}
